import { Component, ViewChild, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DataTableService, FilterService, LocaleService } from '../../../services/services-index';
import { map, startWith, Subscription } from 'rxjs';
import { Enums } from '../../../enums/enums';
import { ExportCsv } from '../../../../_shared/utilities/generate-csv';
import { OrgFilterValue } from '../../../filter/filter.model';
import { ExpandableTable } from '../../../dataTable/expandableTable';
import { IDefaultTableColumn, IExpandableTable } from '../../../models/models';
import * as SharedServices from '../../../services/services-index';
import { ConfigurationService } from '../../../services/config/config.service';
import * as Models from '../../../models/models-index';
import { ComparerService } from '../../../services/utils/comparer.service';
import { heatmapScenarios, tableOptions, totalRowScenarios } from '../../../constants/constants';
import { FeatureFlags } from '../../../services/config/feature.flags';

@Component({
  selector: 'hierarchy-data-table-v5',
  templateUrl: 'hierarchy-data-table.component.html',
  styleUrls: ['./hierarchy-data-table.component.scss'],
  standalone: false
})
export class HierarchyDataTableV5Component extends ExpandableTable implements Models.ElementComponent, IExpandableTable, OnInit, OnDestroy, AfterViewInit {
  @Input() columns: any[] = [];
  @Input() panelConfiguration: Models.PanelConfiguration;
  @Input() settings: Models.ElementSettings;
  @Input() enableSorting: boolean = true;
  @Input() enablePagination: boolean = false;
  @Input() fillHeight: boolean = false;

  isDataSetLoaded$ = this.dataTableService.dataUpdated$.pipe(
    map(value => value !== undefined),
    startWith(false),
  );

  private _options?: Models.OptionModel[];

  @Input()
  set options(value: Models.OptionModel[] | undefined) {
    this._options = value;
    this.updateHeatmapState();
  }

  get options(): Models.OptionModel[] | undefined {
    return this._options;
  }

  private heatmapEnabled: boolean = false;

  @Input() dataSets: Models.DataSet[];

  @Output() drillThroughChange = new EventEmitter<OrgFilterValue>();
  get columnNames() { return this.columns.map(c => c.columnDef); }
  displayedColumns: string[];
  private filterModel: Models.IFilterModel;
  expanded: boolean = false;
  private receivedData: any;
  private showMom = false;
  private showYoy = false;
  dataSource: MatTableDataSource<any>;
  private filterBreadcrumbsForExport: string[];
  private subscriptions: Subscription[] = [];
  locale: string;
  private orgLookupTypeId: number;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  private readonly sortValueSuffix = '__sortValue';
  // Placeholder data for skeleton loader (6 rows)
  placeholderData: any[] = Array.from({ length: 6 }, () => ({}));
  // Fixed columns for the skeleton loader (define explicitly)
  skeletonColumns: string[] = ['col1', 'col2', 'col3', 'col4', 'col5', 'col6'];

  pageIndex = 0;
  pageSize = 25;

  twState = this.tailwindService.observe('lg');

  constructor(
    private dataTableService: DataTableService,
    private filterService: FilterService,
    private configService: ConfigurationService,
    private localeService: LocaleService,
    private router: Router,
    private sharedTranslationService: SharedServices.SharedTranslationService,
    private readonly comparerService: ComparerService,
    private readonly runtimeConfigService: SharedServices.RuntimeConfigService,
    private tailwindService: SharedServices.TailwindService,
  ) {
    super();
  }

  labelTranslator = (val) => this.sharedTranslationService.getLabelTranslation(val, this.locale);

  toggleExpanded(expanded?: boolean) {
    this.expanded = (expanded || !this.expanded);
  }

  ngOnInit() {
    this.expanded = false; //this.dataTableService.heightExpanded;
    this.subscriptions.push(
      this.localeService.locale$.subscribe(loc => this.locale = loc),
      this.dataTableService.toggleHierarchyV5TableExpando$.subscribe(settings => {
        if (settings.expandType === 'height') {
          this.toggleExpanded(settings.expanded)
        }
      }),

      this.dataTableService.dataUpdated$.subscribe((updatedData: {
        data: any[];
        columns: IDefaultTableColumn[];
        type: string;
        initialSort?: { column: string; direction: 'asc' | 'desc' };
      }) => {
        this.receivedData = [];
        this.receivedData = updatedData?.data || [];

        if (!this.receivedData.length) {
          return;
        }

        this.columns = [...updatedData.columns];

        // Precompute the styles and classes
        this.receivedData.forEach(row => {
          this.columns.forEach(column => {
            // Calculate cell classes for each column in the row
            row[`${column.columnDef}_classes`] = this.getCellClasses(column, row);
          });
        });

        // Set the dataSource after all precomputations
        this.dataSource = new MatTableDataSource(this.receivedData.filter(row => row.show));
        if (this.enableSorting) {
          this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string) => this.getSortableValue(data, sortHeaderId);
        }
        // Apply initial sort only once per dataset emission if provided and sorting is enabled
        if (updatedData?.initialSort && this.sort && this.enableSorting) {
          setTimeout(() => {
            if (!this.sort.active) {
              this.sort.active = updatedData.initialSort.column;
              this.sort.direction = updatedData.initialSort.direction;
              this.sort.sortChange.emit({ active: this.sort.active, direction: this.sort.direction });
            }
          });
        }
        this.displayedColumns = this.columns.map(x => x.columnDef);
        this.updateHeatmapState();
      }),
      // do we even need this subscription? It's referenced in old components
      // but old components use old version of the hierarchy table component not this one
      this.dataTableService.selectedTrendUpdated$.subscribe(selectedTrendMetrics => {
        this.applyTableTrendUpdate(selectedTrendMetrics);
      }),
      this.dataTableService.printButtonClicked$.subscribe(data => {
        if (data.printingOption === Enums.printingOptions.pdf) {
          console.log('PDF exporting is not supported in hierarchyDataTable');
        } else {
          this.filterService.requestBreadcrumbString();
          this.excel(data.title, data.expandAll);
        }
      }),
      this.filterService.filterBreadcrumbsReturned$.subscribe(breadcrumbs => {
        this.filterBreadcrumbsForExport = breadcrumbs;
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.dataSource) {
      if (this.paginator) this.dataSource.paginator = this.paginator;
      if (this.sort && this.enableSorting) this.dataSource.sort = this.sort;
    }
  }

  ngOnDestroy(): void {
    this.dataSets = null;
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  applyTableTrendUpdate(selectedTrendMetrics: string[]): void {
    this.showMom = selectedTrendMetrics.includes('MOM');
    this.showYoy = selectedTrendMetrics.includes('YOY');
    this.columns.filter(col => (col.isMom)).forEach(col => { col.show = this.showMom; });
    this.columns.filter(col => (col.isYoy)).forEach(col => { col.show = this.showYoy; });
  }

  updateReport(row) {
    const entity = row.entity;
    const entityType = row.entityType;
    const entityName = row.displayName;
    const orgFilterValue: OrgFilterValue = { levelTypeId: this.orgLookupTypeId, entityType: entityType, value: entity, display: entityName };

    this.drillThroughChange.emit(orgFilterValue);
  }

  toggleChildRows(clickedRow: any): void {
    this.dataTableService.toggleChildRows(clickedRow, this.receivedData, this.columns);
  }
  customTrackBy(index, item) { return item.key; }

  private excel(title: string, expandAll: boolean = false): void {
    let data: string[][] = [[title]];

    // data.push(['Filters:', this.filterBreadcrumbsForExport]);
    // data.push(['Filters:']);

    (this.filterBreadcrumbsForExport || []).forEach(bc => {
      data.push([bc]);
    });

    const cols = this.columns.filter(col => ((col.print === undefined && col.show)
      || (col.print && this.showDealerNameColumn(expandAll))
      || (!this.dealerNameEnabled() && col.print)))
      .map(col => col.header);

    data.push(['']);
    data.push(cols);
    data = data.concat(this.getRowsForExport(expandAll, title));

    const printTitle = title.replace(/-| /g, ''); // Regular expression /-| /g = all instances of ' ' or '-'

    new ExportCsv(data, printTitle);
  }

  private getRowsForExport(expandAll: boolean = false, title: string): string[][] {
    const results: string[][] = [];

    const columns = this.columns.filter((col) => {
      if (((col.print === undefined && col.show) || (col.print && this.showDealerNameColumn(expandAll)) || (!this.dealerNameEnabled() && col.print))) {
        return true;
      } else {
        return false;
      }
    });

    const rowData = !expandAll ? this.dataSource.filteredData : this.receivedData;
    const isDealer = this.configService.role.isDealerRole(this.filterModel.roleLevel);

    rowData.forEach(dataRow => {
      const exportRow: string[] = [];
      columns.forEach(col => {
        // Show entity display name in first column - except for dealers
        if (col.columnDef === 'entity') {
          if (dataRow['entityType'] !== 'dealer') {
            col.printFormatter ? exportRow.push(this.labelTranslator(col.printFormatter(dataRow['displayName']))) : exportRow.push(this.labelTranslator(dataRow['displayName'] || ''));
          } else {
            if (this.filterModel.dealerCode === null)
              exportRow.push(dataRow['entity']);
            else
              exportRow.push('');
          }
        } else if (this.dealerNameEnabled() && col.columnDef === 'displayName') {
          if (dataRow['entityType'] === 'dealer') {
            col.exportFormatter ? exportRow.push(col.exportFormatter(dataRow['displayName'])) : exportRow.push(dataRow['displayName'] || '');
          } else {
            exportRow.push('');
          }
        } else {
          col.printFormatter
            ? exportRow.push(col.printFormatter(dataRow[col.columnDef]))
            : col.formatter
              ? exportRow.push(col.formatter(dataRow[col.columnDef]))
              : exportRow.push(dataRow[col.columnDef] || '');
          //col.formatter ? exportRow.push(col.formatter(dataRow[col.columnDef])) : exportRow.push(dataRow[col.columnDef] || '');
        }
      });
      results.push(exportRow);
    });

    return results;
  }

  private showDealerNameColumn(expandAll: boolean = false): boolean {
    const filteredDataDealerExists = !!this.dataSource.filteredData.find(x => (x.entityType === 'dealer' && x.show === true));

    return this.dealerNameEnabled() && ((expandAll || filteredDataDealerExists));
  };

  private dealerNameEnabled(): boolean {
    const currentUrl = this.router.url;
    const urlsWithDealerNameEnabled: string[] = [
      '/inventory/overview',
      '/leads/overview',
      '/provider-tools',
      '/leads/summary',
      '/sales/summary',
      '/sales/vehicle',
      '/website/aftersales',
      '/website/chat',
      '/website/landingpage',
      '/website/overview',
      '/website/summary',
    ];

    return !!urlsWithDealerNameEnabled.find(u => u?.toUpperCase().includes(currentUrl?.toUpperCase()));
  }

  calculateClasses(column: IDefaultTableColumn, classes?: string[]) {

    // Classes passed in from the front end
    let manualClasses = {
      'hideRow': !column.show
    };

    (classes || []).forEach(mc => {
      manualClasses[mc] = true;
    })

    // Classes from the column config
    let configuredClasses = {};
    (column.columnClasses || []).forEach(cc => {
      configuredClasses[cc] = true;
    })
    // Anything passed into the function directly should be seen as a default that is overridable by configuration
    return {
      ...manualClasses,
      ...configuredClasses
    };
  }

  sortData(sort: Sort) {
    if (!this.enableSorting) return; // completely bypass sorting when disabled
    if (!sort.active || !this.dataSource) return;
    const data = this.dataSource.data.slice();
    const isAsc = sort.direction === 'asc';
    this.dataSource.data = data.sort((a, b) =>
      this.comparerService.compare(
        this.getSortableValue(a, sort.active),
        this.getSortableValue(b, sort.active),
        isAsc));
  }

  getCellClasses(column: any, row: any): { [key: string]: boolean } {
    const baseClasses = this.calculateClasses(column, ['first-data-column']);
    return {
      'heatmap-td': this.heatmapEnabled,
      ...baseClasses
    };
  }

  private updateHeatmapState() {
    const heatmapOption = this.options?.find(o => o.name === tableOptions.heatmap);
    const isGloballyDisabled = this.runtimeConfigService.isFeatureFlagEnabled(FeatureFlags.DisableGlobalHeatMapOption);

    const isExplicitlyDisabled = heatmapOption?.enabled === false;

    this.heatmapEnabled = !isGloballyDisabled && !isExplicitlyDisabled;
  }

  getBackgroundColor(row: any, column: any, heatmapEnabled: boolean): string {
    const heatmapColor = row[column.columnDef + heatmapScenarios.heatmapColorSuffix];

    // Return no color if heatmap is disabled, except for total row (grey)
    if (!heatmapEnabled && heatmapColor !== totalRowScenarios.color) {
      return '';
    }

    return heatmapColor;
  }

  private getSortableValue(row: any, columnId: string): any {
    const sortKey = `${columnId}${this.sortValueSuffix}`;
    if (Object.prototype.hasOwnProperty.call(row, sortKey)) {
      const sortValue = row[sortKey];
      return sortValue ?? row[columnId];
    }

    return row[columnId];
  }
}
